/**
 * `DrawerIndex` — the search screen's zero state, and §8.3 calls it the best screen in
 * the app. `docs/design-direction.md` §8.3.
 *
 * A dense two-column alphabetised list of every artist, riddim and label in the
 * holding: mono `--fs-13`, 28px rows, right-aligned counts, one hairline under each,
 * and six stamped ring chips beneath it. It needs no images, it is genuinely useful
 * before a single key has been pressed, and it tells the user in one glance how deep
 * the crate goes.
 *
 * ## Why the zero state is a real screen and not a hint
 *
 * An empty search box with a "try searching for…" line teaches nothing and offers
 * nowhere to go. The index is the paper drawer card behind the records: it is the
 * answer to "what is actually in here", and every row is a destination. That is also
 * why it loads the same `artists` and `tags` lists the Artist and Tag screens use —
 * the drawer index is a view of the holding, not a bespoke endpoint.
 *
 * ## Three rules this file keeps
 *
 * · **Never a fabricated number** (§8.8, §12.6). A row prints the `trackCount` the API
 *   returned; a row whose count is missing prints no count at all, and there is no
 *   placeholder, no em-dash and no "—" anywhere in this component.
 * · **Every state occupies the same grid.** Loading, error, empty and the loaded index
 *   all render through `StateFrame grid="index"`, whose two-column 28px geometry lives
 *   in `states.module.css`. Nothing jumps when the data arrives.
 * · **28px rows are under the 44px minimum, deliberately.** §8.3 asks for that density
 *   by name; `search.module.css` documents the trade and why no hit expander is added.
 *
 * {@link IndexRow} is exported because the artist, riddim, label and year rows on the
 * *results* side of the search screen are the same object with a highlighted label —
 * one owner for the 28px row, rather than two that drift.
 */

import { useMemo } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';

import type { ArtistSummary, TagSummary } from '@bl/shared';

import type { ApiFailure } from '../lib/api';
import { cx } from '../components/primitives/Rule';
import { TagChip } from '../components/primitives/TagChip';
import { Empty } from '../app/states/Empty';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';

import styles from './search.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/**
 * The six stamped ring chips of §8.3, verbatim.
 *
 * Real scene vocabulary and a real year — `SLENG TENG` is the 1985 riddim that started
 * digital dancehall, not invented patois (§11). All Latin and numerals, which is
 * exactly where the caps tier is licensed (§4.3).
 */
export const DRAWER_TAG_CHIPS: readonly string[] = Object.freeze([
  'DANCEHALL',
  'ROOTS',
  'STEPPERS',
  '1988',
  'SLENG TENG',
  'DUBPLATE',
]);

/** The mono status line while the holding is being read. Sentence case (§4.3, §12.16). */
export const DRAWER_LOADING_RU = 'Читаю ящик';

/* ══ the row ═══════════════════════════════════════════════════════════════════════ */

/**
 * Russian plural of a count, for the row's accessible name.
 *
 * A screen reader that announces "Vybz Kartel 42" has read a number with no unit. The
 * three-form rule is real Russian grammar, not invented copy, and it is the only way
 * to say "42 записи" correctly next to "41 запись" and "45 записей".
 */
export function pluralRu(count: number, forms: readonly [string, string, string]): string {
  const n = Math.abs(Math.trunc(count));
  const tens = n % 100;
  if (tens >= 11 && tens <= 14) return forms[2];
  const ones = n % 10;
  if (ones === 1) return forms[0];
  if (ones >= 2 && ones <= 4) return forms[1];
  return forms[2];
}

const TRACK_FORMS: readonly [string, string, string] = ['запись', 'записи', 'записей'];

/** How many rows carry the out-of-register stagger before the rest appear at once. */
export const STAGGER_CAP = 12;

export interface IndexRowProps {
  /**
   * The printed label. A `ReactNode` because the results side passes a highlighted
   * fragment; the drawer index passes a plain string.
   */
  readonly label: ReactNode;
  /** The plain-text form of {@link label}, for the accessible name. */
  readonly name: string;
  /** The API's own count. `null` prints nothing — never a placeholder (§12.6). */
  readonly count: number | null;
  readonly onSelect: () => void;
  /** Drops the row's own gutter, for the two-column index where the frame owns it. */
  readonly flush?: boolean;
  /** Position in the list, for the 40ms out-of-register stagger. Omit for no stagger. */
  readonly index?: number | undefined;
  readonly className?: string | undefined;
}

/** One 28px mono row: label left, count right, hairline under. */
export function IndexRow({
  label,
  name,
  count,
  onSelect,
  flush = false,
  index,
  className,
}: IndexRowProps): JSX.Element {
  const hasCount = typeof count === 'number' && Number.isFinite(count);
  const ariaLabel = hasCount ? `${name}, ${count} ${pluralRu(count, TRACK_FORMS)}` : name;

  /* A custom property is not part of `CSSProperties`; the value is a plain string. */
  const style =
    index === undefined
      ? undefined
      : ({ '--i': String(Math.min(index, STAGGER_CAP - 1)) } as CSSProperties);

  return (
    <button
      type="button"
      className={cx(
        't-mono',
        't-13',
        styles.cyr,
        styles.row,
        flush && styles.rowFlush,
        index !== undefined && styles.stagger,
        className,
      )}
      style={style}
      aria-label={ariaLabel}
      onClick={onSelect}
    >
      <span className={styles.name}>{label}</span>
      {hasCount ? (
        <span className={styles.count} aria-hidden="true">
          {count}
        </span>
      ) : null}
    </button>
  );
}

/* ══ the index ═════════════════════════════════════════════════════════════════════ */

export type DrawerEntryKind = 'artist' | 'tag';

export interface DrawerEntry {
  readonly kind: DrawerEntryKind;
  readonly id: number;
  readonly name: string;
  readonly trackCount: number;
}

/**
 * Collation for the index.
 *
 * `ru` first because the audience is Russian-speaking and the holding is mixed-script:
 * a plain `<` sorts every Cyrillic name after every Latin one, which is not an index,
 * it is two lists stacked. `sensitivity: 'base'` folds case and accents so `Ámbar` and
 * `ambar` land together, and `numeric` keeps `Riddim 2` before `Riddim 10`.
 */
const COLLATOR = new Intl.Collator(['ru', 'en'], { sensitivity: 'base', numeric: true });

/**
 * Merge the artist and tag lists into one alphabetised index.
 *
 * Exported so the ordering is testable on its own: the merge is the whole feature, and
 * an index that is not actually alphabetised is worse than no index.
 */
export function buildDrawerIndex(
  artists: readonly ArtistSummary[],
  tags: readonly TagSummary[],
): DrawerEntry[] {
  const entries: DrawerEntry[] = [
    ...artists.map((artist): DrawerEntry => ({
      kind: 'artist',
      id: artist.id,
      name: artist.name,
      trackCount: artist.trackCount,
    })),
    ...tags.map((tag): DrawerEntry => ({
      kind: 'tag',
      id: tag.id,
      name: tag.name,
      trackCount: tag.trackCount,
    })),
  ];

  return entries.sort((a, b) => {
    const byName = COLLATOR.compare(a.name, b.name);
    /* A tie is a real possibility — an artist and a label with the same name — so the
       order stays deterministic instead of depending on the sort's stability across
       engines. */
    return byName !== 0 ? byName : a.kind.localeCompare(b.kind) || a.id - b.id;
  });
}

export type DrawerStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DrawerIndexProps {
  readonly artists: readonly ArtistSummary[];
  readonly tags: readonly TagSummary[];
  /** `loading` only paints its own state while there is nothing to show yet. */
  readonly status?: DrawerStatus;
  readonly error?: ApiFailure | null;
  readonly onRetry?: (() => void) | undefined;
  /** Opens the artist screen. */
  readonly onSelectArtist: (artist: ArtistSummary) => void;
  /** Opens the tag screen. */
  readonly onSelectTag: (tag: TagSummary) => void;
  /** A chip fills the chalk line with its word and runs the search. */
  readonly onPickChip: (label: string) => void;
  readonly className?: string | undefined;
}

export function DrawerIndex({
  artists,
  tags,
  status = 'ready',
  error = null,
  onRetry,
  onSelectArtist,
  onSelectTag,
  onPickChip,
  className,
}: DrawerIndexProps): JSX.Element {
  const entries = useMemo(() => buildDrawerIndex(artists, tags), [artists, tags]);

  const artistById = useMemo(() => new Map(artists.map((a) => [a.id, a])), [artists]);
  const tagById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);

  if (entries.length === 0) {
    if (status === 'error' && error !== null) {
      return <ErrorState failure={error} grid="index" onRetry={onRetry} className={className} />;
    }
    if (status === 'loading' || status === 'idle') {
      return <Loading grid="index" status={DRAWER_LOADING_RU} className={className} />;
    }
    /* Ready and genuinely empty: the holding has nothing cut yet. One object, one mono
       line, no action — never an illustration (§8.7, §12.8). */
    return <Empty variant="crate" grid="index" className={className} />;
  }

  return (
    <StateFrame grid="index" state="content" className={className}>
      {entries.map((entry) => (
        <IndexRow
          key={`${entry.kind}:${entry.id}`}
          label={entry.name}
          name={entry.name}
          count={entry.trackCount}
          flush
          onSelect={() => {
            if (entry.kind === 'artist') {
              const artist = artistById.get(entry.id);
              if (artist !== undefined) onSelectArtist(artist);
              return;
            }
            const tag = tagById.get(entry.id);
            if (tag !== undefined) onSelectTag(tag);
          }}
        />
      ))}

      <div className={styles.chips} data-chips="true">
        {DRAWER_TAG_CHIPS.map((label) => (
          <TagChip key={label} label={label} onToggle={() => onPickChip(label)} />
        ))}
      </div>
    </StateFrame>
  );
}
